// Level Up Service - Handles character progression and level-ups
import { UserProfile } from '@/types';
import { karmaService } from './karmaService';
import { questService } from './questService';
import { autonomousEcosystemService } from './autonomousEcosystemService';
import { databaseService } from './databaseService';

interface LevelUpReward {
  type: 'skill' | 'ability' | 'item' | 'access' | 'companion_upgrade';
  name: string;
  description: string;
  value?: number;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
}

interface SkillTree {
  [skillName: string]: {
    level: number;
    maxLevel: number;
    experience: number;
    experienceToNext: number;
    bonuses: string[];
  };
}

interface LevelUpEvent {
  userId: string;
  oldLevel: number;
  newLevel: number;
  rewards: LevelUpReward[];
  skillsUnlocked: string[];
  timestamp: Date;
}

class LevelUpService {
  private readonly baseExperienceRequired = 100;
  private readonly experienceMultiplier = 1.5;
  private readonly maxLevel = 100;

  /**
   * Calculate experience required for a specific level
   */
  public getExperienceRequiredForLevel(level: number): number {
    if (level <= 1) return 0;
    return Math.floor(this.baseExperienceRequired * Math.pow(this.experienceMultiplier, level - 2));
  }

  /**
   * Calculate total experience required to reach a level
   */
  public getTotalExperienceForLevel(level: number): number {
    let total = 0;
    for (let i = 2; i <= level; i++) {
      total += this.getExperienceRequiredForLevel(i);
    }
    return total;
  }

  /**
   * Calculate current level from total experience
   */
  public getLevelFromExperience(totalExperience: number): number {
    let level = 1;
    let experienceUsed = 0;
    
    while (level < this.maxLevel) {
      const experienceForNextLevel = this.getExperienceRequiredForLevel(level + 1);
      if (experienceUsed + experienceForNextLevel > totalExperience) {
        break;
      }
      experienceUsed += experienceForNextLevel;
      level++;
    }
    
    return level;
  }

  /**
   * Get experience progress for current level
   */
  public getExperienceProgress(totalExperience: number): {
    currentLevel: number;
    experienceInCurrentLevel: number;
    experienceRequiredForNext: number;
    progressPercentage: number;
  } {
    const currentLevel = this.getLevelFromExperience(totalExperience);
    const experienceForCurrentLevel = this.getTotalExperienceForLevel(currentLevel);
    const experienceInCurrentLevel = totalExperience - experienceForCurrentLevel;
    const experienceRequiredForNext = this.getExperienceRequiredForLevel(currentLevel + 1);
    const progressPercentage = experienceRequiredForNext > 0 ? 
      (experienceInCurrentLevel / experienceRequiredForNext) * 100 : 100;

    return {
      currentLevel,
      experienceInCurrentLevel,
      experienceRequiredForNext,
      progressPercentage: Math.min(100, progressPercentage)
    };
  }

  /**
   * Award experience and handle level-ups
   */
  public async awardExperience(
    userProfile: UserProfile,
    experienceGained: number,
    source: string
  ): Promise<LevelUpEvent | null> {
    try {
      console.log(`[LevelUp] Awarding ${experienceGained} XP to ${userProfile.displayName} from ${source}`);

      const oldExperience = userProfile.totalExperience || 0;
      const newExperience = oldExperience + experienceGained;
      
      const oldLevel = this.getLevelFromExperience(oldExperience);
      const newLevel = this.getLevelFromExperience(newExperience);

      // Update user profile
      const updatedProfile = {
        ...userProfile,
        totalExperience: newExperience,
        level: newLevel
      };

      // Save to database
      await this.saveUserProgress(updatedProfile);

      // Check if level up occurred
      if (newLevel > oldLevel) {
        console.log(`[LevelUp] Level up! ${oldLevel} → ${newLevel}`);
        
        const levelUpEvent = await this.processLevelUp(updatedProfile, oldLevel, newLevel);
        
        // Notify autonomous ecosystem about level up
        autonomousEcosystemService.updateAgentRelationship('jax', 'player', 5);
        autonomousEcosystemService.updateAgentRelationship('lyra', 'player', 5);
        
        return levelUpEvent;
      }

      return null;
    } catch (error) {
      console.error('[LevelUp] Failed to award experience:', error);
      return null;
    }
  }

  /**
   * Process level up and generate rewards
   */
  private async processLevelUp(
    userProfile: UserProfile,
    oldLevel: number,
    newLevel: number
  ): Promise<LevelUpEvent> {
    const rewards = this.generateLevelUpRewards(newLevel, userProfile.selectedArchetype || 'cyberpunk-hacker');
    const skillsUnlocked = this.getSkillsUnlockedAtLevel(newLevel, userProfile.selectedArchetype || 'cyberpunk-hacker');

    const levelUpEvent: LevelUpEvent = {
      userId: userProfile.id,
      oldLevel,
      newLevel,
      rewards,
      skillsUnlocked,
      timestamp: new Date()
    };

    // Apply rewards to user profile
    await this.applyLevelUpRewards(userProfile, rewards);

    // Generate level-up quest
    await this.generateLevelUpQuest(userProfile, newLevel);

    // Save level up event
    await this.saveLevelUpEvent(levelUpEvent);

    console.log(`[LevelUp] Level up processed for ${userProfile.displayName}:`, levelUpEvent);

    return levelUpEvent;
  }

  /**
   * Generate rewards based on level and archetype
   */
  private generateLevelUpRewards(level: number, archetype: string): LevelUpReward[] {
    const rewards: LevelUpReward[] = [];

    // Base rewards for all levels
    rewards.push({
      type: 'skill',
      name: 'Skill Points',
      description: 'Points to spend on improving your abilities',
      value: Math.floor(level / 5) + 1,
      rarity: 'common'
    });

    // Archetype-specific rewards
    const archetypeRewards = this.getArchetypeSpecificRewards(level, archetype);
    rewards.push(...archetypeRewards);

    // Milestone rewards
    if (level % 10 === 0) {
      rewards.push({
        type: 'companion_upgrade',
        name: 'Companion Enhancement',
        description: 'Your AI companions become more capable and autonomous',
        rarity: 'epic'
      });
    }

    if (level % 25 === 0) {
      rewards.push({
        type: 'access',
        name: 'New District Access',
        description: 'Unlock access to a new area of Neo-Veridia',
        rarity: 'legendary'
      });
    }

    return rewards;
  }

  private getArchetypeSpecificRewards(level: number, archetype: string): LevelUpReward[] {
    const rewards: LevelUpReward[] = [];

    switch (archetype) {
      case 'cyberpunk-hacker':
        if (level % 5 === 0) {
          rewards.push({
            type: 'ability',
            name: 'Advanced Hacking Protocol',
            description: 'Improved success rate and speed for hacking attempts',
            rarity: 'rare'
          });
        }
        break;

      case 'glitch-runner':
        if (level % 5 === 0) {
          rewards.push({
            type: 'ability',
            name: 'Enhanced Mobility',
            description: 'Faster movement and better evasion capabilities',
            rarity: 'rare'
          });
        }
        break;

      case 'synapse-surfer':
        if (level % 5 === 0) {
          rewards.push({
            type: 'ability',
            name: 'Neural Link Upgrade',
            description: 'Deeper connection to the digital realm and AI companions',
            rarity: 'rare'
          });
        }
        break;

      case 'chrome-shaman':
        if (level % 5 === 0) {
          rewards.push({
            type: 'ability',
            name: 'Techno-Mystical Insight',
            description: 'Better understanding of the intersection between technology and consciousness',
            rarity: 'rare'
          });
        }
        break;
    }

    return rewards;
  }

  /**
   * Get skills unlocked at a specific level
   */
  private getSkillsUnlockedAtLevel(level: number, archetype: string): string[] {
    const skills: string[] = [];

    // Universal skills
    if (level === 5) skills.push('Advanced Communication');
    if (level === 10) skills.push('Leadership');
    if (level === 15) skills.push('Strategic Thinking');
    if (level === 20) skills.push('Master Negotiator');

    // Archetype-specific skills
    switch (archetype) {
      case 'cyberpunk-hacker':
        if (level === 3) skills.push('Basic Encryption');
        if (level === 7) skills.push('Network Infiltration');
        if (level === 12) skills.push('AI Manipulation');
        if (level === 18) skills.push('Quantum Hacking');
        break;

      case 'glitch-runner':
        if (level === 3) skills.push('Parkour');
        if (level === 7) skills.push('Stealth Movement');
        if (level === 12) skills.push('Reality Glitching');
        if (level === 18) skills.push('Dimensional Phasing');
        break;

      case 'synapse-surfer':
        if (level === 3) skills.push('Empathic Reading');
        if (level === 7) skills.push('Neural Bridging');
        if (level === 12) skills.push('Consciousness Diving');
        if (level === 18) skills.push('Collective Unconscious Access');
        break;

      case 'chrome-shaman':
        if (level === 3) skills.push('Tech Divination');
        if (level === 7) skills.push('Digital Exorcism');
        if (level === 12) skills.push('Cyber-Spirit Communication');
        if (level === 18) skills.push('Techno-Transcendence');
        break;
    }

    return skills;
  }

  /**
   * Apply level up rewards to user profile
   */
  private async applyLevelUpRewards(userProfile: UserProfile, rewards: LevelUpReward[]): Promise<void> {
    try {
      // Apply skill points
      const skillPointReward = rewards.find(r => r.name === 'Skill Points');
      if (skillPointReward && skillPointReward.value) {
        userProfile.availableSkillPoints = (userProfile.availableSkillPoints || 0) + skillPointReward.value;
      }

      // Apply other rewards (you might want to store these in a separate rewards/inventory system)
      const otherRewards = rewards.filter(r => r.name !== 'Skill Points');
      if (otherRewards.length > 0) {
        // Store rewards in user profile or separate inventory system
        userProfile.unclaimedRewards = [...(userProfile.unclaimedRewards || []), ...otherRewards];
      }

      await this.saveUserProgress(userProfile);
    } catch (error) {
      console.error('[LevelUp] Failed to apply rewards:', error);
    }
  }

  /**
   * Generate a special quest for reaching a new level
   */
  private async generateLevelUpQuest(userProfile: UserProfile, newLevel: number): Promise<void> {
    try {
      const questTitle = `Level ${newLevel} Mastery Challenge`;
      const questDescription = this.generateLevelUpQuestDescription(newLevel, userProfile.selectedArchetype || 'cyberpunk-hacker');

      await questService.createQuest({
        id: `levelup_${userProfile.id}_${newLevel}_${Date.now()}`,
        title: questTitle,
        description: questDescription,
        difficulty: newLevel < 10 ? 'easy' : newLevel < 25 ? 'medium' : 'hard',
        rewards: [`${newLevel * 10} XP`, 'Rare Equipment', 'Skill Enhancement'],
        isActive: true,
        progress: 0,
        maxProgress: 100,
        createdAt: new Date(),
        source: 'level_up_system'
      });

      console.log(`[LevelUp] Generated level-up quest for level ${newLevel}`);
    } catch (error) {
      console.error('[LevelUp] Failed to generate level-up quest:', error);
    }
  }

  private generateLevelUpQuestDescription(level: number, archetype: string): string {
    const baseDescriptions = [
      `Prove your mastery of level ${level} skills by completing a challenging task in Neo-Veridia.`,
      `Your growth to level ${level} has attracted attention. Complete this trial to solidify your reputation.`,
      `The city tests those who reach level ${level}. Show that you're worthy of your new power.`
    ];

    const archetypeSpecific = {
      'cyberpunk-hacker': 'Infiltrate a high-security corporate network and extract valuable data.',
      'glitch-runner': 'Navigate through a reality-distorted zone using your enhanced mobility.',
      'synapse-surfer': 'Establish a deep neural connection with a mysterious AI entity.',
      'chrome-shaman': 'Perform a techno-ritual to balance the digital and spiritual realms.'
    };

    const baseDescription = baseDescriptions[Math.floor(Math.random() * baseDescriptions.length)];
    const specificTask = archetypeSpecific[archetype as keyof typeof archetypeSpecific] || 'Complete a challenging task suited to your abilities.';

    return `${baseDescription} ${specificTask}`;
  }

  /**
   * Calculate experience gain from different activities
   */
  public calculateExperienceGain(activity: string, context: any = {}): number {
    const baseExperience = {
      'story_choice': 10,
      'quest_complete': 50,
      'dialogue_success': 5,
      'exploration': 15,
      'combat_victory': 25,
      'puzzle_solved': 20,
      'social_interaction': 8,
      'discovery': 30,
      'karma_positive': 12,
      'karma_negative': -5
    };

    let experience = baseExperience[activity as keyof typeof baseExperience] || 5;

    // Apply context modifiers
    if (context.difficulty === 'hard') experience *= 1.5;
    if (context.difficulty === 'easy') experience *= 0.8;
    if (context.isFirstTime) experience *= 1.2;
    if (context.karmaBonus) experience += context.karmaBonus;

    return Math.max(1, Math.floor(experience));
  }

  /**
   * Get skill tree for a user
   */
  public getUserSkillTree(userProfile: UserProfile): SkillTree {
    // This would typically be loaded from the database
    // For now, return a basic skill tree structure
    const archetype = userProfile.selectedArchetype || 'cyberpunk-hacker';
    return this.generateSkillTreeForArchetype(archetype, userProfile.level || 1);
  }

  private generateSkillTreeForArchetype(archetype: string, userLevel: number): SkillTree {
    const baseSkills = {
      'Communication': { level: 1, maxLevel: 10, experience: 0, experienceToNext: 100, bonuses: ['Better dialogue options'] },
      'Observation': { level: 1, maxLevel: 10, experience: 0, experienceToNext: 100, bonuses: ['Notice hidden details'] },
      'Problem Solving': { level: 1, maxLevel: 10, experience: 0, experienceToNext: 100, bonuses: ['Creative solutions'] }
    };

    // Add archetype-specific skills
    switch (archetype) {
      case 'cyberpunk-hacker':
        return {
          ...baseSkills,
          'Hacking': { level: Math.min(userLevel, 10), maxLevel: 10, experience: 0, experienceToNext: 150, bonuses: ['Bypass security systems'] },
          'Encryption': { level: Math.min(Math.max(userLevel - 2, 0), 10), maxLevel: 10, experience: 0, experienceToNext: 200, bonuses: ['Secure communications'] },
          'Network Analysis': { level: Math.min(Math.max(userLevel - 5, 0), 10), maxLevel: 10, experience: 0, experienceToNext: 250, bonuses: ['Understand system architecture'] }
        };

      case 'glitch-runner':
        return {
          ...baseSkills,
          'Agility': { level: Math.min(userLevel, 10), maxLevel: 10, experience: 0, experienceToNext: 150, bonuses: ['Enhanced movement speed'] },
          'Stealth': { level: Math.min(Math.max(userLevel - 2, 0), 10), maxLevel: 10, experience: 0, experienceToNext: 200, bonuses: ['Avoid detection'] },
          'Reality Manipulation': { level: Math.min(Math.max(userLevel - 5, 0), 10), maxLevel: 10, experience: 0, experienceToNext: 300, bonuses: ['Exploit environmental glitches'] }
        };

      case 'synapse-surfer':
        return {
          ...baseSkills,
          'Empathy': { level: Math.min(userLevel, 10), maxLevel: 10, experience: 0, experienceToNext: 150, bonuses: ['Understand others\' emotions'] },
          'Neural Interface': { level: Math.min(Math.max(userLevel - 2, 0), 10), maxLevel: 10, experience: 0, experienceToNext: 200, bonuses: ['Better AI communication'] },
          'Consciousness Diving': { level: Math.min(Math.max(userLevel - 5, 0), 10), maxLevel: 10, experience: 0, experienceToNext: 300, bonuses: ['Access deeper mental layers'] }
        };

      case 'chrome-shaman':
        return {
          ...baseSkills,
          'Mysticism': { level: Math.min(userLevel, 10), maxLevel: 10, experience: 0, experienceToNext: 150, bonuses: ['Spiritual insights'] },
          'Tech Integration': { level: Math.min(Math.max(userLevel - 2, 0), 10), maxLevel: 10, experience: 0, experienceToNext: 200, bonuses: ['Blend technology and spirituality'] },
          'Digital Exorcism': { level: Math.min(Math.max(userLevel - 5, 0), 10), maxLevel: 10, experience: 0, experienceToNext: 300, bonuses: ['Cleanse corrupted systems'] }
        };

      default:
        return baseSkills;
    }
  }

  // Database operations
  private async saveUserProgress(userProfile: UserProfile): Promise<void> {
    try {
      await databaseService.updateUserProfile(userProfile.id, {
        level: userProfile.level,
        totalExperience: userProfile.totalExperience,
        availableSkillPoints: userProfile.availableSkillPoints,
        unclaimedRewards: userProfile.unclaimedRewards
      });
    } catch (error) {
      console.error('[LevelUp] Failed to save user progress:', error);
    }
  }

  private async saveLevelUpEvent(event: LevelUpEvent): Promise<void> {
    try {
      // Save to database for analytics and history
      const supabase = databaseService.getSupabase();
      await supabase.from('level_up_events').insert({
        user_id: event.userId,
        old_level: event.oldLevel,
        new_level: event.newLevel,
        rewards: event.rewards,
        skills_unlocked: event.skillsUnlocked,
        created_at: event.timestamp.toISOString()
      });
    } catch (error) {
      console.error('[LevelUp] Failed to save level up event:', error);
    }
  }
}

// Export singleton instance
export const levelUpService = new LevelUpService();